import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatDialogAlertComponent } from '@amwager/feature';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { Observable, map, shareReplay, tap } from 'rxjs';

// Local library
import { TranslationService } from '@amwager/data-access';

@Injectable({
  providedIn: 'root',
})
export class MatNotificationService {
  constructor(
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private router: Router,
    private translationService: TranslationService
  ) {}

  /**
   * Open the shared MatDialogAlert. Existing call sites that ignore the
   * return value keep working; new callers can subscribe to the
   * observable to react to the user's response (true === positive
   * action confirmed, false === dialog closed/negative action).
   *
   * Note: side effects (router redirect on close) still run via an
   * internal subscription so that passive callers don't need to
   * subscribe to make redirectUrl work.
   *
   * @param {string} title - Dialog title
   * @param {string} message - Dialog message body
   * @param {string} subtitle - Optional subtitle
   * @param {string} actionPositiveText - Label for the confirm button
   * @param {string} actionNegativeText - Label for the cancel/close button
   * @param {string} redirectUrl - If set, router navigate after close
   * @param {boolean} isSubmit - Reserved (unused; kept for API stability)
   * @param {any} payload - Arbitrary payload echoed into the dialog data
   * @returns {Observable<boolean>} - Emits true if positive action, false otherwise
   */
  showAlert(
    title: string,
    message: string,
    subtitle?: string,
    actionPositiveText?: string,
    actionNegativeText?: string,
    redirectUrl?: string,
    isSubmit?: boolean,
    payload?: any
  ): Observable<boolean> {
    // Callers pass English copy rather than language keys, so translate here:
    // it keeps every alert in the user's language without touching call sites.
    const copy = (text?: string) =>
      text === undefined ? text : this.translationService.translateCopy(text);

    const dialogRef = this.dialog.open(MatDialogAlertComponent, {
      data: {
        title: copy(title),
        subtitle: copy(subtitle),
        message: copy(message),
        actionPositiveText: copy(actionPositiveText),
        actionNegativeText: copy(actionNegativeText),
        payload
      },
      // Match WagerConfirmDialog / MatConfirmDialog (geolocation)
      // so every dialog the user sees has the same footprint.
      // `maxWidth` keeps the dialog from blowing through the
      // viewport on narrow phones where 340px would be too wide.
      width: '340px',
      maxWidth: '95vw',
      disableClose: true,
      autoFocus: false,
      // Trim Material's default 150ms enter / 75ms exit so the
      // Continue / Close buttons feel instant. Without this the
      // wager-limit and geo-location alerts feel sluggish to dismiss.
      enterAnimationDuration: '120ms',
      exitAnimationDuration: '0ms',
      panelClass: 'custom-alert-dialog'
    });

    const result$ = dialogRef.afterClosed().pipe(
      map((res) => res === true),
      tap(() => {
        if (redirectUrl)
          this.router.navigate([redirectUrl]);
      }),
      // Replay the latest value so passive callers (no subscribe) and
      // late subscribers (e.g. wager-limit "Continue" handler) both
      // receive the user's response without re-opening the dialog.
      shareReplay(1)
    );

    // Eagerly subscribe so the redirectUrl side-effect always runs even
    // if no caller subscribes to the returned observable.
    result$.subscribe();

    return result$;
  }

  hide(): void {
    this.dialog.closeAll();
  }

  showSnackbar(
    msg : string,
    action : string = 'Close',
    duration :number = 3000, // default to 3s,
    otherParams : object = {}
  ) : void {

    this.snackBar.open(this.translationService.translateCopy(msg),
      this.translationService.translateCopy(action), {
      duration,   // Shorthand for duration: duration
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
      panelClass: ['success-snackbar'],
      ...otherParams
    });

  }
}
